import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BsLinkedin } from "react-icons/bs";
import { BellRing, ChevronDown, Send, X } from "lucide-react";
import { FloatingMenu, MENU_ITEM } from "../../components/ui/FloatingMenu";
import { LinkedinMoment, LinkedinTemplate, ProspectRow } from "../../interfaces/linkedinProspecting";
import { Button } from "../../components/ui/Button";
import { Avatar } from "./parts";
import { MESSAGE_MOMENTS, followUpDate, momentFor, momentOf } from "./vocabulary";
import { Me, fillVariables, variableValues } from "./variables";
import { VariableTextarea } from "./VariableTextarea";

type Moment = Exclude<LinkedinMoment, "invitation">;

/** After a first message or a relance, the next nudge is due unless they answer — 3 days by default. */
const DEFAULT_FOLLOW_UP: Partial<Record<Moment, number>> = { first_message: 3, followup: 7 };

/**
 * Writing to a prospect once connected — the invitation is behind, this is the conversation:
 * the moment their stage calls for (first message, relance, proposing a call, after the meeting),
 * its templates filled for them, the text editable. « Message » (LinkedIn's own button) copies the text
 * and opens the profile (LinkedIn has to send it, never us), ticks « Message envoyé » on a first
 * message, and sets the next follow-up if one is picked.
 */
export default function MessageDialog({
    row, me, templates, onManage, onClose, onSent,
}: {
    row: ProspectRow;
    /** The rep, for {votre offre} and their own variables. */
    me: Me;
    /** Every LinkedIn template; only the message ones are offered here. */
    templates: LinkedinTemplate[];
    onManage: () => void;
    onClose: () => void;
    onSent: (row: ProspectRow, moment: Moment, text: string, followUpAt: string | null) => Promise<void>;
}) {
    const [moment, setMoment] = useState<Moment>(momentFor(row.state) || "first_message");
    const [templateId, setTemplateId] = useState<string | null>(null);
    const [text, setText] = useState("");
    const [followUpDays, setFollowUpDays] = useState<number | null>(DEFAULT_FOLLOW_UP[moment] ?? null);
    const [sending, setSending] = useState(false);
    const templatesTrigger = useRef<HTMLButtonElement>(null);
    const [templatesOpen, setTemplatesOpen] = useState(false);
    const closeTemplates = useCallback(() => setTemplatesOpen(false), []);
    /** A template picked from « mes templates » for another moment: that moment opens on it, not its first. */
    const chosenRef = useRef<string | null>(null);

    /** The rep's own first, the last edited on top; the untouched defaults after. */
    const ordered = useMemo(() => {
        const edited = (t: LinkedinTemplate) => (t.updatedAt ? Date.parse(t.updatedAt) : 0);
        return [...templates].sort((a, b) => Number(!!a.isDefault) - Number(!!b.isDefault) || edited(b) - edited(a));
    }, [templates]);
    const mine = useMemo(() => ordered.filter((t) => !t.isDefault), [ordered]);
    const forMoment = useMemo(() => ordered.filter((t) => momentOf(t) === moment), [ordered, moment]);
    const values = useMemo(() => variableValues(row, me), [row, me]);

    // Each moment starts from its first template — variables kept, shown coloured, filled on send.
    // Switching moment starts over.
    useEffect(() => {
        const first = forMoment.find((t) => t._id === chosenRef.current) || forMoment[0];
        chosenRef.current = null;
        setTemplateId(first?._id || null);
        setText(first ? first.text : "");
        setFollowUpDays(DEFAULT_FOLLOW_UP[moment] ?? null);
    }, [moment, forMoment]);

    useEffect(() => {
        const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", esc);
        return () => document.removeEventListener("keydown", esc);
    }, [onClose]);

    const resolved = fillVariables(text, values);
    const suggested = momentFor(row.state);

    const send = async () => {
        setSending(true);
        try {
            await onSent(row, moment, resolved, followUpDays ? followUpDate(followUpDays) : null);
            onClose();
        } finally {
            setSending(false);
        }
    };

    /**
     * A message template of another moment opens that moment on it; one of the current moment, or an
     * invitation text of mine, just fills the editor.
     */
    const pickTemplate = (t: LinkedinTemplate) => {
        closeTemplates();
        const target = momentOf(t);
        if (target !== "invitation" && target !== moment) {
            chosenRef.current = t._id;
            setMoment(target);
            return;
        }
        setTemplateId(t._id);
        setText(t.text);
    };
    const momentLabel = (t: LinkedinTemplate) => MESSAGE_MOMENTS.find((m) => m.key === momentOf(t))?.label || "Invitation";

    const current = MESSAGE_MOMENTS.find((m) => m.key === moment)!;
    const pill = (on: boolean) =>
        `rounded-md px-2.5 py-1 font-sans text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
            on ? "bg-surface text-ink shadow-sm" : "text-ink-muted hover:text-ink"
        }`;

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 sm:items-center sm:p-4" onMouseDown={onClose}>
            <div
                role="dialog"
                aria-modal="true"
                aria-label={`Écrire à ${row.name}`}
                onMouseDown={(e) => e.stopPropagation()}
                className="flex max-h-screen w-full max-w-xl flex-col overflow-hidden rounded-t-2xl bg-surface shadow-float sm:max-h-[90vh] sm:rounded-2xl"
            >
                {/* Who it goes to, like the head of a LinkedIn conversation */}
                <div className="flex items-center gap-3 border-b border-border px-5 py-4">
                    <Avatar name={row.name} />
                    <div className="min-w-0 flex-1">
                        <a
                            href={row.linkedinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex max-w-full items-center gap-1.5 font-sans text-base font-semibold text-ink hover:underline"
                        >
                            <span className="truncate">{row.name}</span>
                            <BsLinkedin className="h-3.5 w-3.5 shrink-0 text-info" />
                        </a>
                        <p className="truncate font-sans text-xs text-ink-muted">
                            {[row.headline, row.establishment.name].filter(Boolean).join(" · ") || "Message LinkedIn"}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-lg p-1.5 text-ink-faint hover:bg-surface-sunken hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                        aria-label="Fermer"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
                    {/* Moment: a compact switch, the suggested one marked */}
                    <div>
                        <div role="tablist" aria-label="Moment" className="flex gap-1 overflow-x-auto rounded-lg bg-surface-sunken p-1">
                            {MESSAGE_MOMENTS.map((m) => (
                                <button
                                    key={m.key}
                                    role="tab"
                                    type="button"
                                    aria-selected={moment === m.key}
                                    onClick={() => setMoment(m.key)}
                                    className={`relative flex-1 whitespace-nowrap ${pill(moment === m.key)}`}
                                >
                                    {m.label}
                                    {suggested === m.key && (
                                        <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-accent" aria-label="suggéré" />
                                    )}
                                </button>
                            ))}
                        </div>
                        <p className="mt-1.5 font-sans text-xs text-ink-muted">
                            {current.hint}
                            {suggested === moment && <span className="text-accent"> · suggéré pour son étape</span>}
                        </p>
                    </div>

                    {/* The message: this moment's templates, the rest of mine one click away, then the editor */}
                    <div>
                        <div className="mb-2 flex items-center gap-2">
                            <div className="flex min-w-0 flex-1 flex-wrap gap-1">
                                {forMoment.map((t) => (
                                    <button
                                        key={t._id}
                                        type="button"
                                        onClick={() => {
                                            setTemplateId(t._id);
                                            setText(t.text);
                                        }}
                                        className={`rounded-full border px-2.5 py-0.5 font-sans text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                                            templateId === t._id ? "border-ink bg-ink text-ink-inverse" : "border-border text-ink-muted hover:border-border-strong hover:text-ink"
                                        }`}
                                    >
                                        {t.name}
                                    </button>
                                ))}
                            </div>
                            <button
                                ref={templatesTrigger}
                                type="button"
                                onClick={() => setTemplatesOpen((o) => !o)}
                                aria-haspopup="menu"
                                aria-expanded={templatesOpen}
                                className="inline-flex shrink-0 items-center gap-0.5 rounded font-sans text-xs font-medium text-ink-muted underline-offset-2 transition-colors hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                            >
                                Choisir parmi mes templates
                                <ChevronDown className="h-3.5 w-3.5" />
                            </button>
                            <FloatingMenu anchor={templatesTrigger} open={templatesOpen} onClose={closeTemplates} width={300}>
                                <div className="max-h-80 overflow-y-auto">
                                    {[
                                        { key: "mine", label: "Mes templates", list: mine, tagged: true },
                                        ...MESSAGE_MOMENTS.map((m) => ({
                                            key: m.key,
                                            label: `${m.label} · par défaut`,
                                            list: ordered.filter((t) => t.isDefault && momentOf(t) === m.key),
                                            tagged: false,
                                        })),
                                    ].map((section) =>
                                        section.list.length ? (
                                            <div key={section.key} className="py-1">
                                                <p className="px-3 pb-1 pt-1.5 font-sans text-[11px] font-semibold uppercase tracking-wide text-ink-faint">{section.label}</p>
                                                {section.list.map((t) => (
                                                    <button
                                                        key={t._id}
                                                        role="menuitem"
                                                        onClick={() => pickTemplate(t)}
                                                        className={`${MENU_ITEM} flex-col !items-start !gap-0`}
                                                    >
                                                        <span className="flex w-full items-baseline gap-2">
                                                            <span className="truncate font-medium">{t.name}</span>
                                                            {section.tagged && <span className="ml-auto shrink-0 text-[11px] text-ink-faint">{momentLabel(t)}</span>}
                                                        </span>
                                                        <span className="line-clamp-1 text-xs text-ink-muted">{t.text}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        ) : null
                                    )}
                                </div>
                                <div className="border-t border-border pt-1">
                                    <button
                                        role="menuitem"
                                        onClick={() => {
                                            closeTemplates();
                                            onManage();
                                        }}
                                        className={MENU_ITEM}
                                    >
                                        Gérer mes templates…
                                    </button>
                                </div>
                            </FloatingMenu>
                        </div>

                        <VariableTextarea
                            value={text}
                            onChange={setText}
                            values={values}
                            minRows={6}
                            placeholder={forMoment.length ? "" : "Aucun template pour ce moment : écrivez votre message, ou choisissez parmi vos templates."}
                        />
                    </div>

                    {/* Next follow-up, one line */}
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 font-sans text-xs font-medium text-ink-muted">
                            <BellRing className="h-3.5 w-3.5" />
                            Relancer si pas de réponse
                        </span>
                        <div className="flex rounded-lg bg-surface-sunken p-0.5">
                            {[{ label: "Non", days: null as number | null }, { label: "3 j", days: 3 }, { label: "1 sem", days: 7 }, { label: "2 sem", days: 14 }].map((p) => (
                                <button key={p.label} type="button" onClick={() => setFollowUpDays(p.days)} aria-pressed={followUpDays === p.days} className={pill(followUpDays === p.days)}>
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-3 border-t border-border px-5 py-3">
                    <p className="min-w-0 flex-1 font-sans text-xs text-ink-faint">
                        <span className="ml-1 font-mono">{resolved.length} car.</span>
                    </p>
                    <Button size="sm" variant="ghost" onClick={onClose} disabled={sending}>
                        Annuler
                    </Button>
                    <Button
                        size="sm"
                        onClick={send}
                        disabled={sending || !resolved.trim()}
                        className="!h-8 !rounded-full !bg-[#0a66c2] !px-3.5 !text-sm !font-semibold !text-white hover:!bg-[#004182]"
                    >
                        <Send className="h-4 w-4" />
                        Message
                    </Button>
                </div>
            </div>
        </div>
    );
}
